import type { ReactNode } from 'react';

/**
 * #664 (F73) — the shell of every page before the sign-in: Login, Register,
 * Forgot password, Reset password, Verify email and the end of a Google
 * sign-in. A centred column, the brand header (the logo ring, the title and
 * the subtitle) and the card. Six pages wrote this column by hand, with four
 * sizes of the logo.
 *
 * - `title` is the brand header. A page with no title shows the card only: a
 *   result screen (`AuthResult`) needs no header.
 * - `wide` is the Register form: two columns of names need more room.
 * - `LogoIcon` is the mark as an icon of `AuthResult`.
 *
 * Only this file writes `min-h-[70vh]`, the logo ring and `/logo.webp` for the
 * auth pages (`dry-phase2-664.spec.ts`).
 */
export function LogoIcon({ className = 'w-12 h-12' }: { className?: string }) {
  return <img src="/logo.webp" alt="LMF" className={`rounded-full object-cover ${className}`} />;
}

interface AuthCardProps {
  title?: string;
  subtitle?: string;
  wide?: boolean;
  children: ReactNode;
}

export default function AuthCard({ title, subtitle, wide = false, children }: AuthCardProps) {
  return (
    <div className={`min-h-[70vh] flex items-center justify-center${wide ? ' py-8' : ''}`}>
      <div className={`w-full ${wide ? 'max-w-md' : 'max-w-sm'}`}>
        {title && (
          <div className="text-center mb-8">
            <div className="w-24 h-24 rounded-full bg-white border border-gray-100 shadow-sm p-1 mx-auto mb-4">
              <img src="/logo.webp" alt="LMF" className="w-full h-full rounded-full object-cover" />
            </div>
            <h1 className="text-xl font-black text-gray-900">{title}</h1>
            {subtitle && <p className="text-gray-500 text-sm mt-1">{subtitle}</p>}
          </div>
        )}
        <div className="card p-6 space-y-4">{children}</div>
      </div>
    </div>
  );
}
